/**
 * Unified API Manager
 * Central hub for all API services providing:
 * - Service registration and access
 * - Unified error handling
 * - Cross-service status monitoring
 * - Request/response logging
 */
import SearchApiService from './SearchApiService';
import AnalyticsApiService from './AnalyticsApiService';
import UserApiService from './UserApiService';
import { isDevelopment } from '../../config/environment';

class ApiManager {
  constructor() {
    this.services = {
      search: SearchApiService,
      analytics: AnalyticsApiService,
      user: UserApiService
    };
    
    this.statusListeners = [];
    
    // Don't start monitoring immediately - wait until after login
    // This prevents connectivity tests from slowing down the login process
    
    console.log('[ApiManager] Unified API manager initialized with services:', Object.keys(this.services));
  }
  
  /**
   * Get a specific API service
   * @param {string} serviceName - Name of the service (search, analytics, user)
   * @returns {Object} - The requested API service instance
   */
  getService(serviceName) {
    const service = this.services[serviceName];
    if (!service) {
      throw new Error(`Unknown API service: ${serviceName}. Available services: ${Object.keys(this.services).join(', ')}`);
    }
    return service;
  }
  
  /**
   * Make a request through a specific service
   * @param {string} serviceName - Name of the service
   * @param {string} method - HTTP method
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request data
   * @param {Object} config - Additional configuration
   * @returns {Promise<Object>} - Standardized API response
   */
  async request(serviceName, method, endpoint, data = null, config = {}) {
    const service = this.getService(serviceName);
    
    try {
      console.log(`[ApiManager] ${serviceName.toUpperCase()}.${method.toUpperCase()} ${endpoint}`);
      
      // Use the service's request method
      if (typeof service.request === 'function') {
        return await service.request(method, endpoint, data, config);
      } else {
        // For services without base request method (like UserApiService)
        const methodName = method.toLowerCase();
        if (typeof service[methodName] === 'function') {
          return await service[methodName](endpoint, data, config);
        } else {
          throw new Error(`Method ${method} not supported by ${serviceName} service`);
        }
      }
    } catch (error) {
      console.error(`[ApiManager] ${serviceName} request failed:`, error);
      throw error;
    }
  }
  
  /**
   * Test connectivity for all services
   * @returns {Promise<Object>} - Status for all services
   */
  async testAllConnectivity() {
    console.log('[ApiManager] Testing connectivity for all services...');
    
    const results = {};
    
    for (const [serviceName, service] of Object.entries(this.services)) {
      try {
        results[serviceName] = await service.testConnectivity();
      } catch (error) {
        results[serviceName] = {
          isOnline: false,
          error: error.message
        };
      }
    }
    
    console.log('[ApiManager] Connectivity test results:', results);
    
    // Notify listeners of status changes
    this.notifyStatusListeners(results);
    
    return results;
  }
  
  /**
   * Get current status for all services
   * @returns {Object} - Current status of all services
   */
  getAllStatus() {
    const status = {};
    
    for (const [serviceName, service] of Object.entries(this.services)) {
      if (typeof service.getStatus === 'function') {
        status[serviceName] = service.getStatus();
      } else {
        status[serviceName] = {
          serviceName,
          isOnline: service.isOnline || false,
          lastChecked: service.lastChecked || null,
          baseURL: service.baseURL || 'unknown'
        };
      }
    }
    
    return status;
  }
  
  /**
   * Start periodic status monitoring
   */
  startStatusMonitoring(interval = 30000) {
    console.log('[ApiManager] Starting periodic status monitoring...');
    
    // Initial connectivity test
    this.testAllConnectivity();
    
    // Set up periodic monitoring
    this.statusInterval = setInterval(() => {
      this.testAllConnectivity();
    }, interval);
  }
  
  /**
   * Stop periodic status monitoring
   */
  stopStatusMonitoring() {
    if (this.statusInterval) {
      clearInterval(this.statusInterval);
      this.statusInterval = null;
      console.log('[ApiManager] Stopped periodic status monitoring');
    }
  }
  
  /**
   * Add a listener for status changes
   * @param {Function} listener - Function to call when status changes
   */
  addStatusListener(listener) {
    this.statusListeners.push(listener);
  }
  
  /**
   * Remove a status listener
   * @param {Function} listener - Listener to remove
   */
  removeStatusListener(listener) {
    this.statusListeners = this.statusListeners.filter(l => l !== listener);
  }
  
  /**
   * Notify all status listeners
   * @param {Object} status - Current status data
   */
  notifyStatusListeners(status) {
    this.statusListeners.forEach(listener => {
      try {
        listener(status);
      } catch (error) {
        console.error('[ApiManager] Error in status listener:', error);
      }
    });
  }
  
  /**
   * Convenience methods for common operations
   */
  
  // Search API shortcuts
  async searchLoads(filters) {
    return this.getService('search').searchLoads(filters);
  }
  
  async searchMultiCity(filters) {
    return this.getService('search').searchMultiCity(filters);
  }
  
  // Analytics API shortcuts
  async getFreightData(filters) {
    return this.getService('analytics').getFreightData(filters);
  }
  
  async getBrokerMetrics(filters) {
    return this.getService('analytics').getBrokerMetrics(filters);
  }
  
  async getRateInsights(filters) {
    return this.getService('analytics').getRateInsights(filters);
  }
  
  async getLanePricing(laneData) {
    return this.getService('analytics').getLanePricing(laneData);
  }
  
  async getRouteOptimization(routeData) {
    return this.getService('analytics').getRouteOptimization(routeData);
  }
  
  // User API shortcuts
  async getUser(user, accessToken) {
    return this.getService('user').getOrCreateUser(user, accessToken);
  }
  
  /**
   * Cleanup method
   */
  destroy() {
    this.stopStatusMonitoring();
    this.statusListeners = [];
    console.log('[ApiManager] API manager destroyed');
  }
}

// Create and export singleton instance
const apiManager = new ApiManager();
export default apiManager;

// Export individual services for backward compatibility
export { SearchApiService, AnalyticsApiService, UserApiService };